import React, { useCallback, useEffect, useRef, useState } from "react";
import { batch, useDispatch, useSelector } from "react-redux";
import { ApiService, CommitmentService, ProjectDocumentService } from "../../api";
import { SegFlags, translate } from "../../common/providers";
import { exportView } from "../../navigation";
import CommitmentTrackingContent from "./CommitmentTrackingContent";
import { DetectionBanner } from "./components";
import CommitmentHeader from "./components/commitment-header/CommitmentHeader";
import {
	setSelectedTheme,
	setSelectedThemeUuid,
	setSelectedCommitment,
	setFilters,
	setAppliedFilters,
	resetState,
	setCommitmentFilters,
	setPossibleValues,
	setOngoingProgress,
	setHasLaunchFinished,
	fetchCommitments,
	setPage,
} from "./slice/commitmentTrackingSlice";
import styles from "./CommitmentTracking.module.css";
import { FIELD_TYPES } from "./constants";
import { useApi, useEffectOnce } from "../../common/hooks";
import { withWebsocket } from "../../common/components";

export const CommitmentTracking = ({ subscribeWS }) => {
	const projectId = useSelector(({ context }) => context?.project?.id);
	const appliedFilters = useSelector(({ commitmentTracking }) => commitmentTracking.appliedFilters);
	const sliceProgress = useSelector(({ commitmentTracking }) => commitmentTracking.progress);
	const [checkedProgress, setCheckedProgress] = useState(false);
	const [projectHasDocuments, setProjectHasDocuments] = useState(false);
	const [launchProgress, setLaunchProgress] = useState(null);
	const [isInitializing, setIsInitializing] = useState(false);
	const [hasNoCommitments, setHasNoCommitments] = useState(false);
	const hasLaunchFinished = useSelector(({ commitmentTracking }) => commitmentTracking.hasLaunchFinished);
	const { call: hasCommitment } = useApi(CommitmentService.hasCommitment);
	const { call: getCommitmentFilters } = useApi(CommitmentService.getCommitmentFilters);
	const { call: getPossibleValuesOfField } = useApi(CommitmentService.getPossibleValuesOfField);
	const { call: getProgressSnapshot } = useApi(CommitmentService.checkLaunch);
	const { call: getProjectHasDocuments } = useApi(ProjectDocumentService.projectHasDocuments);
	const dispatch = useDispatch();

	const cancelTokenSourceRef = useRef(null);

	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);

	const initializeCommitments = useCallback(
		async (afterLaunch) => {
			setIsInitializing(true);
			try {
				const response = await hasCommitment({ projectId });
				if (response?.data?.value ?? response?.value ?? response?.data) {
					const filterResponse = await getCommitmentFilters({ projectId });
					if (filterResponse) {
						dispatch(setCommitmentFilters(filterResponse));
						const newFilters = { separator: "AND" };
						dispatch(setFilters(newFilters));
						dispatch(setAppliedFilters(newFilters));
						const categoryFilters = filterResponse.filter((filter) =>
							[FIELD_TYPES.CATEGORY, FIELD_TYPES.USER].includes(filter.type)
						);
						batch(() => {
							categoryFilters.forEach((category) => {
								if (category.type !== FIELD_TYPES.USER) {
									getPossibleValuesOfField({ projectId, fieldUuid: category.fieldUuid }, newFilters)
										.then((data) => {
											dispatch(setPossibleValues(data));
										})
										.catch(console.error);
								}
							});
							/** Replaces empty state after launch for 'all commitments' */
							if (afterLaunch) {
								const cancelToken = cancelTokenSourceRef.current?.token;
								dispatch(setSelectedTheme(translate("commitment-tracking.theme-list.all")));
								dispatch(setSelectedThemeUuid(null));
								dispatch(setSelectedCommitment(null));
								dispatch(setPage(0));
								dispatch(
									fetchCommitments({
										projectId,
										payload: {
											...appliedFilters,
											theme: { is: true, matchAll: false, elements: [] },
										},
										page: 0,
										limit: 25,
										cancelToken,
									})
								);
							}
							/** _______________________________________________________ */
						});
						setHasNoCommitments(false);
					}
				} else {
					setHasNoCommitments(true);
				}
			} catch (error) {
				console.error("Error initializing commitments:", error);
				setHasNoCommitments(true);
			} finally {
				setIsInitializing(false);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[dispatch, getCommitmentFilters, getPossibleValuesOfField, hasCommitment, projectId]
	);
	useEffect(() => {
		const documentTitle = translate("commitment-tracking.document.title");
		document.title = documentTitle;
		if (projectId) {
			if (checkedProgress) {
				initializeCommitments();
			}
			getProjectHasDocuments({ projectId })
				.then((data) => {
					setProjectHasDocuments(data);
				})
				.catch(console.error);
		}
		return () => {
			dispatch(resetState());
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, initializeCommitments, projectId, checkedProgress]);
	/** Websocket handling : progress bar */
	const handleFinishProgress = () => {
		setLaunchProgress(null);
		dispatch(setOngoingProgress(null));
		initializeCommitments(true);
		dispatch(setHasLaunchFinished(true));
	};
	const handleProgress = ({ reason, progress }) => {
		if (reason === "COMPLETED_SUCCESS" && progress === "100%") {
			handleFinishProgress();
		}
		if (reason === "STARTED" && progress) {
			setLaunchProgress({ progress });
			dispatch(setOngoingProgress({ progress }));
		}
	};
	const handleWebsocketMessage = ({ featureName, progress, reason }) => {
		if (featureName === "COMMITMENT") {
			handleProgress({ reason, progress });
		}
	};
	const handleLaunch = () => {
		dispatch(setHasLaunchFinished(false));
		setLaunchProgress({ progress: "0%" });
		dispatch(setOngoingProgress({ progress: "0%" }));
	};
	useEffect(() => {
		subscribeWS(`/topic/ai-agent/projects/${projectId}/refresh`, handleWebsocketMessage);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [subscribeWS, projectId]);
	useEffectOnce(
		() => {
			if (sliceProgress) {
				setLaunchProgress(sliceProgress);
			}
			getProgressSnapshot({ projectId })
				.then(({ value }) => {
					if (value !== null) {
						const payload = { progress: value };
						setLaunchProgress(payload);
						dispatch(setOngoingProgress(payload));
						if (value === "100%") {
							handleFinishProgress();
						}
					}
				})
				.catch(console.error)
				.finally(() => {
					setCheckedProgress(true);
				});
		},
		[sliceProgress],
		() => !launchProgress
	);
	/** *********************************** */
	const isLoading = isInitializing && !launchProgress;
	const hasFinishedAndEmpty = !isLoading && hasNoCommitments;
	return (
		<div className={styles.container}>
			<CommitmentHeader
				hasNoCommitments={hasNoCommitments}
				launchProgress={launchProgress}
				projectHasDocuments={projectHasDocuments}
				onLaunch={handleLaunch}
			/>
			{(!!launchProgress || hasLaunchFinished) && (
				<DetectionBanner
					hasLaunchFinished={hasLaunchFinished}
					progress={launchProgress?.progress}
					onCloseBanner={() => dispatch(setHasLaunchFinished(false))}
				/>
			)}
			{isLoading && (
				<div className={styles.emptyStateContainer}>
					<div>{translate("commitment-tracking.empty-state.loading")}</div>
				</div>
			)}
			{hasFinishedAndEmpty && (
				<div className={styles.emptyStateContainer}>
					<div>
						{translate(
							(!launchProgress &&
								((!hasLaunchFinished && "commitment-tracking.empty-state.no-commitments") ||
									"commitment-tracking.detection-banner.title.finished")) ||
								"commitment-tracking.detection.in-progress"
						)}
					</div>
				</div>
			)}
			{!isInitializing && !hasNoCommitments && <CommitmentTrackingContent />}
		</div>
	);
};

export { default as commitmentTrackingSlice } from "./slice/commitmentTrackingSlice";

export default exportView({
	path: "/projects/:projectId/commitment-tracking",
	component: withWebsocket(CommitmentTracking),
	localesPath: "/commitment-tracking/locales",
	segFlag: SegFlags.COMMITMENT,
	requiresCommitmentAccess: true,
});
